// use2FA.js
// Encapsulates all 2FA state: OTP send, countdown, resend cooldown, method toggle.
// Returns the pieces the confirm screen needs.

import { useState, useEffect } from "react";
import { toast } from "sonner";
import client from "../../../API";

/**
 * @param {object|null} currentUser - from useAuth()
 * @returns {{
 *   otpCode, setOtpCode,
 *   otpMethod, setOtpMethod,
 *   otpSent, otpSending,
 *   otpError, setOtpError,
 *   otpCountdown, resendCooldown,
 *   sendOtp: (method?, amount?) => void,
 *   reset: () => void,
 * }}
 */
export function use2FA(currentUser) {
  const [otpCode,        setOtpCode]        = useState("");
  const [otpMethod,      setOtpMethod]      = useState("email");
  const [otpSent,        setOtpSent]        = useState(false);
  const [otpSending,     setOtpSending]     = useState(false);
  const [otpError,       setOtpError]       = useState("");
  const [otpCountdown,   setOtpCountdown]   = useState(0);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => { if (otpCountdown   > 0) { const t = setTimeout(() => setOtpCountdown((p)   => p - 1), 1000); return () => clearTimeout(t); } }, [otpCountdown]);
  useEffect(() => { if (resendCooldown > 0) { const t = setTimeout(() => setResendCooldown((p) => p - 1), 1000); return () => clearTimeout(t); } }, [resendCooldown]);

  const sendOtp = async (method = otpMethod, amount = 0) => {
    if (resendCooldown > 0) return;
    setOtpSending(true); setOtpError("");
    const payload = {
      user_id:          currentUser?.user_id || currentUser?.id,
      creator_username: currentUser?.creator_username,
    };
    try {
      if (method === "sms") {
        await client.twofa.sendSMS(payload);
        setOtpSent(true); setOtpCountdown(300); setResendCooldown(30);
        toast.success("Code sent via SMS");
      } else {
        await client.twofa.sendTransferOTP({ ...payload, amount });
        setOtpSent(true); setOtpCountdown(600); setResendCooldown(60);
        toast.success("Code sent to your email");
      }
    } catch (error) {
      const d   = error.response?.data;
      // Automatic fallback: SMS unavailable → email
      if (method === "sms" && d?.fallback === "email") {
        setOtpMethod("email"); sendOtp("email", amount); return;
      }
      const msg = d?.error || "Failed to send verification code";
      setOtpError(msg); toast.error(msg);
    } finally {
      setOtpSending(false);
    }
  };

  const reset = () => {
    setOtpCode(""); setOtpMethod("email"); setOtpSent(false); setOtpSending(false);
    setOtpError(""); setOtpCountdown(0); setResendCooldown(0);
  };

  return {
    otpCode, setOtpCode,
    otpMethod, setOtpMethod,
    otpSent, otpSending,
    otpError, setOtpError,
    otpCountdown, resendCooldown,
    sendOtp, reset,
  };
}